/**
 * localMedia — the two facts that let a seller SEE their picture before it has finished travelling.
 *
 * WHY THIS EXISTS (founder, 2026-09-15: "le chargement des images et video doit etre rapide, le seller
 * ne vas pas attendre 5 min pour chaque image"). The image control used to write the block's value only
 * once the upload had landed, so the CANVAS — the thing the seller is actually composing — stayed empty
 * for the whole trip while the finished picture sat in the inspector panel, read from their own disk.
 *
 * Measured on this machine the same day: the front shrinks a 12.6 MB PNG to 152 KB in 0.76 s, the droplet
 * answers a media read in ~0.35 s and its CPU idles under 15 %. None of that is slow. What IS slow is the
 * seller's uplink, which has been measured swinging by a factor of 30 — and `xhr.upload.onprogress`
 * reports bytes handed to the SOCKET, not bytes received, so on a small body the bar jumps to 100 %
 * instantly and the entire real transfer hides behind the word "Finishing up…". The founder's screenshot
 * is exactly that: a full bar, frozen.
 *
 * So the fix is not to make the bytes faster (they already are). It is to stop making the seller wait for
 * them at all: the block takes the LOCAL `blob:` URL the moment the file is picked, and the delivery URL
 * replaces it when the upload lands.
 *
 * THE DANGER, and the reason this file exists rather than a line in the control: a `blob:` URL is alive
 * only in the tab that minted it. Persisted, it is a broken image for everyone — the seller's own reload
 * included — and published, it is a broken image on a real storefront. So it must be IMPOSSIBLE to save
 * one, not merely unusual: [stripLocalMedia] runs inside `builderApi.saveDraft`, the single writer every
 * path goes through (autosave, global chrome, theme workspace, page duplication). A local URL that
 * reaches the wire is a bug we cannot have; a local URL dropped from a save is simply an image whose
 * upload has not landed yet, and the next save — the one the real URL triggers — carries it.
 */

/** URL schemes that live and die with this tab. Never persist one. */
export function isLocalMediaUrl(value: unknown): boolean {
  return typeof value === 'string' && (value.startsWith('blob:') || value.startsWith('data:'));
}

/**
 * The key that MAKES a media value: if this one is local, the whole value is a placeholder and goes.
 * An image with no `url`, or a video source with no `url`, is not a smaller image — it is nothing.
 */
const PRIMARY_URL_KEYS = ['url', 'src'] as const;

/**
 * Keys that DECORATE a media value. A locally-captured poster on an already-uploaded clip is worth
 * dropping on its own — the video still plays, it just starts without a still.
 */
const SECONDARY_URL_KEYS = [
  'poster',
  'posterUrl',
  'smallUrl',
  'thumbnail',
  'thumbnailUrl',
] as const;

/**
 * Deep-copy `value` with every locally-scoped media reference removed.
 *
 * Returns the SAME reference when there was nothing to strip — the common case by far, and what keeps
 * autosave's dirty marker and the render context from churning on identity.
 *
 * Rules, applied at every depth:
 *  - an object whose `url`/`src` is local  → dropped whole (and removed from its array / its parent key);
 *  - an object whose `poster`/`posterUrl`/… is local → that key alone is removed;
 *  - an array → cleaned entry by entry, dropped entries removed (a `sources[]` that empties collapses
 *    to nothing via the rule above, because the object then has no primary url left).
 */
export function stripLocalMedia<T>(value: T): T {
  return clean(value) as T;
}

function clean(value: unknown): unknown {
  if (Array.isArray(value)) return cleanArray(value);
  if (value !== null && typeof value === 'object')
    return cleanObject(value as Record<string, unknown>);
  return value;
}

/** `undefined` marks "this entry is gone", which is why it can never be a legitimate cleaned value. */
const DROP = undefined;

function cleanArray(list: unknown[]): unknown[] {
  let changed = false;
  const out: unknown[] = [];
  for (const entry of list) {
    const next = clean(entry);
    if (next === DROP && entry !== DROP) {
      changed = true;
      continue;
    }
    if (next !== entry) changed = true;
    out.push(next);
  }
  return changed ? out : list;
}

function cleanObject(obj: Record<string, unknown>): unknown {
  // A value identified by a local primary url is a placeholder standing in for an upload in flight.
  for (const key of PRIMARY_URL_KEYS) {
    if (isLocalMediaUrl(obj[key])) return DROP;
  }

  let changed = false;
  let emptiedSources = false;
  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(obj)) {
    if ((SECONDARY_URL_KEYS as readonly string[]).includes(key) && isLocalMediaUrl(entry)) {
      changed = true;
      continue;
    }
    const next = clean(entry);
    if (next === DROP && entry !== DROP) {
      // A nested media value vanished (e.g. an image setting whose upload is still in flight). Drop the
      // KEY rather than writing an explicit `undefined`: the block then falls back to its own default,
      // which is what a never-uploaded image has always looked like.
      changed = true;
      continue;
    }
    if (next !== entry) changed = true;
    // A rendition list that emptied leaves a media value with nothing to play or show. Saving that is
    // saving a video block pointing at no video, so the value goes with it.
    if (key === 'sources' && Array.isArray(entry) && entry.length > 0 && isEmptyArray(next)) {
      emptiedSources = true;
    }
    out[key] = next;
  }
  if (emptiedSources) return DROP;
  return changed ? out : obj;
}

function isEmptyArray(value: unknown): boolean {
  return Array.isArray(value) && value.length === 0;
}

/**
 * The same rule, for the THEME row — which [stripLocalMedia] cannot enforce on its own.
 *
 * `ThemeSettings.brand.favicon` is a BARE URL string, not a media object with a `url` key, so the deep
 * walk above has nothing to recognise: a `blob:` favicon would sail straight through. And the theme
 * never goes near `saveDraft`; it has its own versionless channel (`saveTheme.ts`), which serializes the
 * whole object. So the guard has to be stated here, once, and called by every theme writer — the live
 * PUT, the workspace PUT, and the keepalive PUT that fires when the tab closes mid-upload.
 *
 * Dropping the key means the store reads as "no favicon" for the seconds the upload is in flight; the
 * save that the delivery URL triggers puts it back. Persisting the `blob:` instead would mean a favicon
 * that is broken for everyone INCLUDING the seller as soon as they reload.
 */
export function stripLocalThemeMedia<T extends { brand?: { favicon?: string } }>(theme: T): T {
  if (!isLocalMediaUrl(theme.brand?.favicon)) return theme;
  const rest = { ...(theme.brand ?? {}) };
  delete rest.favicon;
  return { ...theme, brand: rest };
}
